import { Suspense } from 'react'
import { CatchBoundary } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { RollingFigure } from './digit-roll'

/**
 * The boundary for the hero's two figures, HERE rather than in the route: the route hands over a `<Suspense>`-wrapped
 * reader as `children`, and until it resolves — or forever, if it fails — not one figure is shown. There is no zero
 * to fall back to, because a placeholder 0 is a number a visitor believes.
 *
 * Loading keeps the block's HEIGHT, invisibly: the hero's columns are centred against each other, so counters with
 * no height re-centre the left one and carry the title and description with them. A failure keeps nothing.
 */
export function LandingCounts(props: { children: React.ReactNode }) {
  return (
    <CatchBoundary getResetKey={() => 'landing-counts'} errorComponent={() => null}>
      <Suspense fallback={<CountsPlaceholder />}>{props.children}</Suspense>
    </CatchBoundary>
  )
}

/**
 * The counters' exact height and nothing else: the real block, rendered `invisible` so no figure can be read
 * off it. The figures are built the same way whatever the count is, so a 0 here measures what the true number
 * will measure — which is why this reserves the space rather than guessing at a line height.
 */
function CountsPlaceholder() {
  return (
    <div aria-hidden className="invisible">
      <LandingCountsValue profilesCount={0} companiesCount={0} />
    </div>
  )
}

/**
 * The counters themselves, once the route has read the counts. One per line, so neither ever shifts the other,
 * and each an odometer (`./digit-roll.tsx`): identical markup on both sides, rolled into place by CSS.
 */
export function LandingCountsValue(props: { profilesCount: number; companiesCount: number }) {
  const { t } = useLingui()
  return (
    // No `Appear` of its own: the hero already fades this block in (index.tsx) and the roll IS the entrance —
    // a second 600 ms fade over it only hid the first third of the climb.
    <div className="flex flex-col gap-2.5">
      <RollingFigure value={props.profilesCount} label={t`profiles analyzed`} startMs={LEAD_IN_MS} />
      <RollingFigure value={props.companiesCount} label={t`registered companies`} startMs={LEAD_IN_MS + TRAIL_MS} />
    </div>
  )
}

/**
 * The two figures OVERLAP, the second trailing the first by a beat (docs/notes/app/landing.md).
 *
 * The lead-in is set by the hero's own entrance: this block is the last of four to fade in (`Appear` at 240 ms
 * over 600 ms), and starting sooner runs the first columns underneath that fade, two opacities deep. The trail
 * is only a beat because a figure that is there to persuade cannot be hidden until three seconds in.
 */
const LEAD_IN_MS = 650
const TRAIL_MS = 800
